"use client";

import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { campaignService } from "../voice-services/campaignService";
import { useAuth } from "../contexts/AuthContext";

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const toast = useToast();
  const { credits, isAdmin, currentClientId, clients } = useAuth();

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({
        title: "Campaign name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsCreating(true);
    try {
      let selectedCompanyName: string | undefined;
      if (isAdmin && currentClientId) {
        const selectedClient = clients.find(
          (client) => client.id === currentClientId
        );
        selectedCompanyName = selectedClient?.company_name;
        console.log(
          "Admin creating campaign for company:",
          selectedCompanyName
        );
      }

      await campaignService.create({ name: name.trim() }, selectedCompanyName);
      toast({
        title: "Campaign created successfully",
        status: "success",
        duration: 3000,
      });
      setName("");
      onClose();
      onSuccess();
    } catch (error) {
      toast({
        title: "Error creating campaign",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Campaign Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter campaign name"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            color="black"
            _hover={{ bg: "gray.100" }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            onClick={handleCreate}
            isLoading={isCreating}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to create campaign"
                : undefined
            }
          >
            Create Campaign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCampaignModal;
